import { useListEquipment } from "#hooks/adapters/useEquipment";
import { useListFacilities } from "#hooks/adapters/useFacilities";
import {
  FLOW_QUERY_KEY,
  useGetManySingleDownstreamFlow,
  useGetManySingleUpstreamFlow,
  useListFlows,
} from "#hooks/adapters/useFlows";
import { useFlowHeaders } from "#hooks/tables/useFlowHeaders";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import { FlowDetailRoute } from "#routes/organization/flows/[flowId]/detail";
import { FlowsTableFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink/RoutingLink";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToEquipmentDetail } from "#src/routes/organization/equipment/[equipmentId]";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
} from "#src/utils/table";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  Icon,
  StorageKeys,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  FlowAdapter,
  FlowAdapterQueryKeys,
  FlowV2Type,
  isResponseErrorType,
  Resources,
  SortDirection,
  type FlowType,
  type GroupedByEntryType,
  type ResponseErrorType,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPForbiddenError,
  HTTPNotFoundError,
  HTTPResponseError,
} from "@validereinc/utilities";
import pLimit from "p-limit";
import React, { useCallback, useMemo, useState } from "react";

// TODO: depend on Zod schema directly
const FLOW_KEYS = {
  NAME: "name",
  TYPE: "type",
  PRODUCT_CATEGORY: "product_category",
  PRODUCT_TYPE: "product_type",
  ASSOCIATED_FACILITY: "associated_facility_id",
  ASSOCIATED_EQUIPMENT: "associated_equipment_id",
  DESTINATION_ASSET: "destination_asset",
  SOURCE_ASSET: "source_asset",
};

export const AssociatedFlowTablePanel = ({
  id,
  type,
  viewConfigStorageKey,
  groupByTablesStateStorageKey,
}: {
  id: string;
  type: "equipment" | "facility";
} & Pick<
  StorageKeys,
  "viewConfigStorageKey" | "groupByTablesStateStorageKey"
>) => {
  const { localize } = useLocalization();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      [type, "flows"]
    );
  const { customAttributeHeaders, isLoading: isCustomAttributesLoading } =
    useCustomAttributeFiltersV2({
      assetType: AssetType.FLOW,
      groupable: true,
    });
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<Record<string, FlowType>>(
    {}
  );
  const [viewFilters] = useStickyState<any>({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState<{ name?: string }>(
    {},
    filterConfigStorageKey
  );

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: FLOW_KEYS.NAME,
      sortDirection: SortDirection.ASCENDING,
      pageSize: 25,
    },
    { name, type, id },
    tableStateStorageKey
  );

  const flowListPayload: Parameters<typeof FlowAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: {
      // @ts-expect-error TODO: fix filter typing
      $or:
        type === "equipment"
          ? [
              { associated_equipment_id: id } as any,
              { downstream_equipment_id: id },
              { upstream_equipment_id: id },
            ]
          : [
              { associated_facility_id: id },
              { downstream_facility_id: id },
              { upstream_facility_id: id },
            ],
      ...tableFilters,
    },
    meta: {
      period: getYearMonthFromDateRange(viewFilters.period),
    },
  };

  const { data, isLoading, isFetching } = useListFlows(flowListPayload);
  const items = data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...flowListPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? flowListPayload)
  );

  const flowGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: FlowAdapterQueryKeys.getList(d.payload),
      queryFn: () => FlowAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? flowGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof FlowAdapter.deleteOne>[0]) => {
      return FlowAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.FLOW)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: [FLOW_QUERY_KEY] });
    },
    onError: (error: any) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.FLOW)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.FLOW)}. An unknown error occurred.`,
        });
      }
    },
  });

  const flowIdsWithSingleUpstream = items.reduce(
    (acc: string[], { upstream_flows_count, id }) => {
      if (upstream_flows_count === 1) {
        acc.push(id);
      }
      return acc;
    },
    []
  );
  const upstreamFlowsQuery = useGetManySingleUpstreamFlow({
    ids: flowIdsWithSingleUpstream,
  });
  const isUpstreamLoading = upstreamFlowsQuery.some(
    (result) => result.isLoading
  );
  const upstreamFlowsMap = upstreamFlowsQuery.reduce<
    Record<string, FlowV2Type>
  >((accumulator, current, index) => {
    const flowId = flowIdsWithSingleUpstream[index];
    if (current?.data) {
      accumulator[flowId] = current?.data as unknown as FlowV2Type;
    }
    return accumulator;
  }, {});

  const flowIdsWithSingleDownstream = items.reduce(
    (acc: string[], { downstream_flows_count, id }) => {
      if (downstream_flows_count === 1) {
        acc.push(id);
      }
      return acc;
    },
    []
  );
  const downstreamFlowsQuery = useGetManySingleDownstreamFlow({
    ids: flowIdsWithSingleDownstream,
  });
  const isDownstreamLoading = downstreamFlowsQuery.some(
    (result) => result.isLoading
  );

  const downstreamFlowsMap = downstreamFlowsQuery.reduce<
    Record<string, FlowV2Type>
  >((accumulator, current, index) => {
    const flowId = flowIdsWithSingleDownstream[index];
    if (current?.data) {
      accumulator[flowId] = current?.data as unknown as FlowV2Type;
    }
    return accumulator;
  }, {});

  /** TODO: Remove these once the flows API returns the upstream/downstream facility & equipment detail */
  const facilityIds = items
    .flatMap(({ upstream_facility_id, downstream_facility_id }) => [
      upstream_facility_id,
      downstream_facility_id,
    ])
    .filter((item) => item);
  const listFacilities = useListFacilities(
    { filters: { id: facilityIds } },
    { enabled: !!facilityIds.length }
  );
  const facilities = listFacilities.data?.data ?? [];

  const equipmentIds = items
    .flatMap(({ upstream_equipment_id, downstream_equipment_id }) => [
      upstream_equipment_id,
      downstream_equipment_id,
    ])
    .filter((item) => item);
  const listEquipment = useListEquipment(
    { filters: { id: equipmentIds as string[] } },
    { enabled: !!equipmentIds.length }
  );
  const equipment = listEquipment.data?.data ?? [];
  const { isLoading: isLoadingFlowHeaders, headers: flowHeaders } =
    useFlowHeaders({
      groupable: true,
    });

  const getItemBulkActions = useCallback(
    ({ buttonProps }) => [
      <IsFeatureAvailable
        key="delete"
        featureFlagQuery="core:flows"
        permissionQuery="flows:delete"
        fallbackChildren={null}
      >
        <Button
          {...buttonProps}
          slotLeft={<Icon variant="trash" />}
          variant="error-outline"
          onClick={() => setIsDeleteConfirmationOpen(true)}
        >
          Delete
        </Button>
      </IsFeatureAvailable>,
    ],
    []
  );

  const headers = useMemo(
    () => [
      ...flowHeaders,
      {
        label: "Origin",
        key: FLOW_KEYS.SOURCE_ASSET,
        variant: "link",
        renderComponent: ({ item }: { item: FlowV2Type }) => {
          const upstreamEquipment = equipment.find(
            ({ id }) => id === item?.upstream_equipment_id
          );
          const upstreamFacility = facilities.find(
            ({ id }) => id === item?.upstream_facility_id
          );
          const hasUpstreamFlows =
            item.upstream_flows_count && item.upstream_flows_count > 0;

          if (item.downstream_flows_count && item.downstream_flows_count > 1) {
            return "Many";
          }

          if (!hasUpstreamFlows && !upstreamFacility && !upstreamEquipment) {
            return "-";
          }

          let pathname: string, value: string;

          if (upstreamEquipment) {
            pathname = linkToEquipmentDetail(upstreamEquipment.id);
            value = upstreamEquipment.name;
          } else if (upstreamFacility) {
            pathname = linkToFacilityDetail(upstreamFacility.id);
            value = upstreamFacility.name;
          } else if (hasUpstreamFlows) {
            const upstreamFlow = upstreamFlowsMap[item.id];

            if (!upstreamFlow) {
              return "-";
            }

            pathname = FlowDetailRoute.toLink({
              pathParams: { flowId: upstreamFlow.id },
            });
            value = upstreamFlow.name;
          } else {
            return "-";
          }

          return <RoutingLink to={pathname}>{value}</RoutingLink>;
        },
      },
      {
        label: "Destination",
        key: FLOW_KEYS.DESTINATION_ASSET,
        renderComponent: ({ item }: { item: FlowV2Type }) => {
          const downstreamEquipment = equipment.find(
            ({ id }) => id === item?.downstream_equipment_id
          );
          const downstreamFacility = facilities.find(
            ({ id }) => id === item?.downstream_facility_id
          );
          const hasDownstreamFlow =
            item.downstream_flows_count && item.downstream_flows_count > 0;

          if (item.downstream_flows_count && item.downstream_flows_count > 1) {
            return "Many";
          }

          if (
            !hasDownstreamFlow &&
            !downstreamFacility &&
            !downstreamEquipment
          ) {
            return "-";
          }

          let pathname: string, value: string;

          if (downstreamEquipment) {
            pathname = linkToEquipmentDetail(downstreamEquipment.id);
            value = downstreamEquipment.name;
          } else if (downstreamFacility) {
            pathname = linkToFacilityDetail(downstreamFacility.id);
            value = downstreamFacility.name;
          } else if (hasDownstreamFlow) {
            const downstreamFlow = downstreamFlowsMap[item.id];

            if (!downstreamFlow) {
              return "-";
            }

            pathname = FlowDetailRoute.toLink({
              pathParams: { flowId: downstreamFlow.id },
            });
            value = downstreamFlow.name;
          } else {
            return "-";
          }

          return <RoutingLink to={pathname}>{value}</RoutingLink>;
        },
      },
      ...customAttributeHeaders,
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [flowHeaders, customAttributeHeaders, metaHeaders, metaExtendedHeaders]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    getItemId: (item) => item.id,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: flowGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    totalSelectableItems,
    getItemBulkActions,
    selected: selectedRows,
    onSelectionChange: setSelectedRows,
  });

  return (
    <>
      <DataTablePanel
        panelProps={{ title: localize("flow_plural") }}
        storageKey={tableConfigStorageKey}
        filterComponent={
          <FlowsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            hasSubAssetFilters={false}
          />
        }
        dataTableProps={{
          intent: "simplicity-first",
          headers: headers as Array<HeaderType<FlowType>>,
          items: items as FlowType[],
          isLoading:
            isLoading ||
            isDownstreamLoading ||
            isUpstreamLoading ||
            isLoadingFlowHeaders ||
            isCustomAttributesLoading,
          isBusy: isFetching,
          getItemId: (item) => item.id,
          getItemBulkActions,
          selected: selectedRows,
          onSelectionChange: setSelectedRows,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
        }}
        {...groupByDataTableProps}
      />
      <IsFeatureAvailable
        featureFlagQuery="core:flows"
        permissionQuery="flows:delete"
        fallbackChildren={null}
      >
        <Dialog
          title={`Delete ${localize(`${Resources.FLOW}_plural`)}?`}
          isOpen={isDeleteConfirmationOpen}
          onClose={() => setIsDeleteConfirmationOpen(false)}
          actionRow={[
            <Button
              key="submit"
              variant="error"
              isLoading={deleteMutation.isLoading}
              onClick={() => {
                const selectedRowIds = Object.keys(selectedRows);
                const limitPromises = pLimit(8);

                setIsDeleteConfirmationOpen(false);
                toast
                  .pushAsync(
                    Promise.all(
                      selectedRowIds.map((id) =>
                        limitPromises(async () =>
                          deleteMutation.mutateAsync({
                            id,
                            previousData: selectedRows[id],
                          })
                        )
                      )
                    ),
                    {
                      intent: "note",
                      description: `Deleting ${selectedRowIds.length} selected ${localize(`${Resources.FLOW}_plural`)}...`,
                      isBusy: deleteMutation.isLoading,
                    },
                    {
                      isDismissible: false,
                      duration: Infinity,
                    }
                  )
                  .then(({ data, toast: firstToast }) => {
                    toast.push(
                      {
                        intent: "success",
                        description: `Successfully deleted ${data.length} ${localize(`${Resources.FLOW}_plural`)}`,
                      },
                      {
                        id: firstToast.id,
                        isDismissible: true,
                        duration: 5000,
                      }
                    );
                    setSelectedRows({});
                  })
                  .catch(({ error, toastId }) => {
                    const isNotFoundError = error instanceof HTTPNotFoundError;
                    const isForbiddenError =
                      error instanceof HTTPForbiddenError;
                    const isNodeAPIError =
                      error instanceof HTTPResponseError &&
                      isResponseErrorType(error.responseBodyJson);

                    toast.push(
                      {
                        intent: "error",
                        description: isNodeAPIError
                          ? `Failed to delete selected ${localize(`${Resources.FLOW}_plural`)}: ${(error.responseBodyJson as unknown as ResponseErrorType | undefined)?.error}`
                          : isNotFoundError || isForbiddenError
                            ? `Failed to delete selected ${localize(`${Resources.FLOW}_plural`)}: ${error.getExplanation()}`
                            : `Failed to delete ${localize(`${Resources.FLOW}_plural`)}.`,
                      },
                      {
                        id: toastId,
                        isDismissible: true,
                        duration: 5000,
                      }
                    );
                  })
                  .finally(() => {
                    queryClient.invalidateQueries({
                      queryKey: [FLOW_QUERY_KEY],
                    });
                  });
              }}
            >
              Delete
            </Button>,
          ]}
        >
          You&apos;re deleting {Object.keys(selectedRows).length}&nbsp;
          {localize(`${Resources.FLOW}_plural`)}. This action cannot be undone.
          Are you sure you want to continue?
        </Dialog>
      </IsFeatureAvailable>
    </>
  );
};
